import React from "react";
import { Pencil, ExternalLink, Mail, Phone, Globe, MapPin, User, Building2, FileText, Package } from "lucide-react";
import { fmtDate } from "../../lib/permissions";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";

function DetailField({ icon: Icon, label, value, href, external, multiline }) {
  const empty = value == null || value === "" || value === "—";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </div>
      {empty ? (
        <div className="text-sm text-neutral-400">—</div>
      ) : href ? (
        <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer noopener" : undefined}
          className="text-sm text-primary hover:underline break-all inline-flex items-center gap-1">
          {value}{external && <ExternalLink className="w-3 h-3" />}
        </a>
      ) : (
        <div className={`text-sm text-neutral-800 ${multiline ? "whitespace-pre-wrap" : "break-words"}`}>{value}</div>
      )}
    </div>
  );
}

export default function ContactDetail({ contact, onClose, onEdit, fields, canEditContact }) {
  if (!contact) return null;

  const productRows = Array.isArray(contact.products) && contact.products.length
    ? contact.products
    : (contact.product_name || contact.product_code
        ? [{ product_name: contact.product_name, product_code: contact.product_code, volume: contact.volume }]
        : []);

  const addressParts = [contact.address, contact.city, contact.state, contact.zip_code].filter(Boolean);
  const fullAddress = addressParts.join(", ");
  const displayName = [contact.title, contact.contact_person].filter(Boolean).join(" ") || "—";

  return (
    <Dialog open={!!contact} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contact-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">
            <div className="flex flex-wrap items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              <span>{contact.company_name || "—"}</span>
              {(contact.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
            </div>
            <div className="text-sm font-normal text-muted-foreground mt-1 flex items-center gap-2">
              <User className="w-3.5 h-3.5" />
              {displayName}{contact.designation ? ` — ${contact.designation}` : ""}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Contact info */}
        <section className="space-y-2 text-sm">
          <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Contact Information</h4>
          <div className="grid grid-cols-2 gap-3">
            <DetailField icon={Mail} label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : null} />
            <DetailField icon={Phone} label="Phone" value={contact.phone} href={contact.phone ? `tel:${contact.phone.replace(/\s+/g, "")}` : null} />
            <DetailField icon={Globe} label="Website" value={contact.website}
              href={contact.website ? (contact.website.startsWith("http") ? contact.website : `https://${contact.website}`) : null}
              external />
            <DetailField icon={MapPin} label="Address" value={fullAddress || "—"} multiline />
          </div>
        </section>

        {/* Products & volumes */}
        <section className="space-y-2 mt-4">
          <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
            <Package className="w-3 h-3" />Products &amp; Codes {productRows.length > 1 && <span className="text-primary">({productRows.length})</span>}
          </h4>
          {productRows.length === 0 ? (
            <div className="text-sm italic text-muted-foreground border border-dashed border-border rounded-md p-3">No products linked to this contact yet.</div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Product Name</th>
                    <th className="text-left px-3 py-2">Product Code</th>
                    <th className="text-left px-3 py-2">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {productRows.map((p) => (
                    <tr key={`${p.product_name || ""}-${p.product_code || ""}-${p.volume || ""}`} className="border-t border-border">
                      <td className="px-3 py-2">{p.product_name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{p.product_code || "—"}</td>
                      <td className="px-3 py-2">{p.volume || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Remarks */}
        {contact.remarks && (
          <section className="space-y-1 mt-4">
            <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
              <FileText className="w-3 h-3" />Remarks
            </h4>
            <div className="border border-border rounded-md bg-secondary/40 p-3 whitespace-pre-wrap text-sm">{contact.remarks}</div>
          </section>
        )}

        {/* Custom fields */}
        {(fields || []).length > 0 && (fields || []).some((cf) => contact.custom_fields?.[cf.name]) && (
          <section className="space-y-2 mt-4">
            <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Additional Fields</h4>
            <div className="grid grid-cols-2 gap-3">
              {(fields || []).map((cf) => {
                const v = contact.custom_fields?.[cf.name];
                if (!v) return null;
                return <DetailField key={cf.id} label={cf.label} value={v} />;
              })}
            </div>
          </section>
        )}

        {/* Meta */}
        <section className="mt-5 pt-3 border-t border-border grid grid-cols-3 gap-3 text-xs text-muted-foreground">
          <div><span className="uppercase tracking-widest text-[9px]">Owner</span><div className="text-neutral-800 mt-0.5">{contact.created_by_name || "—"}</div></div>
          {contact.lead_id && (
            <div className="col-span-2">
              <span className="uppercase tracking-widest text-[9px]">Source</span>
              <div className="mt-0.5">
                <a href="/grab-a-deal" data-testid="contact-lead-link" className="text-primary hover:underline">
                  Grabbed from the Grab A Deal board
                </a>
              </div>
            </div>
          )}
          <div><span className="uppercase tracking-widest text-[9px]">Created</span><div className="text-neutral-800 mt-0.5">{fmtDate(contact.created_at)}</div></div>
          <div><span className="uppercase tracking-widest text-[9px]">Last Updated</span><div className="text-neutral-800 mt-0.5">{fmtDate(contact.updated_at)}</div></div>
        </section>

        <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          {canEditContact && (
            <Button type="button" data-testid="contact-detail-edit"
              onClick={() => { onEdit(contact); onClose(); }}
              className="bg-primary hover:bg-primary/90 text-white">
              <Pencil className="w-4 h-4 mr-1.5" />Edit Contact
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
